import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { api, type FormDetail, type FormioSchema } from './api';
import { AppShell } from './AppShell';
import { loginRequest } from './auth/msal';
import { resolveFormId } from './config';

// Load the heavy formio builder (~2 MB) as a separate chunk, only when a form is opened.
const FormioBuilder = lazy(() => import('./FormioBuilder').then((m) => ({ default: m.FormioBuilder })));

function Editor({ formId }: { formId: string }) {
  const [form, setForm] = useState<FormDetail | null>(null);
  const [schema, setSchema] = useState<FormioSchema>({});
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Warm the formio chunk in parallel with the form fetch so its download/parse
    // overlaps the API call instead of starting only after the form arrives.
    void import('./FormioBuilder');
    api
      .getForm(formId)
      .then((f) => {
        setForm(f);
        setSchema(f.draft?.schema ?? {});
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load form'));
  }, [formId]);

  const onChange = useCallback((next: FormioSchema) => setSchema(next), []);

  async function save() {
    setSaving(true);
    setStatus(null);
    setError(null);
    try {
      const updated = await api.saveDraftSchema(formId, schema);
      setForm(updated);
      setStatus('Saved to draft.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (error && !form) {
    return <div className="wrap"><p className="error">{error}</p></div>;
  }
  if (!form) {
    return <div className="wrap">Loading…</div>;
  }
  if (!form.draft) {
    return (
      <div className="wrap">
        <h1>{form.title}</h1>
        <p className="note">
          This form has no open draft. Create a draft in the management app (New draft), then reopen the builder.
        </p>
      </div>
    );
  }

  return (
    <div className="wrap">
      <div className="header">
        <h1>{form.title} <span className="ver">draft v{form.draft.versionNumber}</span></h1>
        <div className="actions">
          {status && <span className="success">{status}</span>}
          {error && <span className="error">{error}</span>}
          <button disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save to draft'}</button>
        </div>
      </div>
      <Suspense fallback={<p className="note">Preparing editor…</p>}>
        <FormioBuilder key={form.draft.id} initialSchema={schema} onChange={onChange} />
      </Suspense>
    </div>
  );
}

export function App() {
  const { instance } = useMsal();
  const formId = resolveFormId();

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="wrap center">
          <h1>Form Builder</h1>
          <p>Sign in with your organisation account to edit forms.</p>
          <button onClick={() => void instance.loginRedirect(loginRequest)}>Sign in</button>
        </div>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <AppShell>
          {formId ? (
            <Editor formId={formId} />
          ) : (
            <div className="wrap">
              <h1>Form Builder</h1>
              <p className="note">Open a specific form with <code>?formId=&lt;id&gt;</code> (the management app links here).</p>
            </div>
          )}
        </AppShell>
      </AuthenticatedTemplate>
    </>
  );
}
